/**
 * Metadata Types for VIDA System
 *
 * Types for the hierarchical metadata system with permission delegation.
 */

// Permission levels (hierarchical: none < read < write < admin)
export type GrantLevel = 'none' | 'read' | 'write' | 'admin';

// Scope types for values and permissions
export type ScopeType = 'system' | 'organization' | 'user';

// Base permission type (original from static data)
export type BasePermission = 'system' | 'organization' | 'user';

// ============================================================================
// DOMAIN
// ============================================================================

export interface MetadataDomain {
  _id: string;
  id: string;
  name: string;
  description: string;
  icon: string;
  order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  // Populated on detail view
  trees?: MetadataTree[];
}

// ============================================================================
// TREE
// ============================================================================

export interface MetadataTree {
  _id: string;
  id: string;
  domain_id: string;
  name: string;
  description: string;
  base_permission: BasePermission;
  is_system: boolean;
  order: number;
  created_at: string;
  updated_at: string;
  // Populated on detail view
  nodes?: MetadataNode[];
}

// ============================================================================
// NODE
// ============================================================================

export interface MetadataNode {
  _id: string;
  id: string;
  tree_id: string;
  parent_id: string | null;
  name: string;
  description?: string;
  base_permission: BasePermission;
  path: string;
  depth: number;
  ancestor_ids: string[];
  order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  // Populated on list views
  children?: MetadataNode[];
  // Calculated by backend based on user permissions
  effective_permission?: GrantLevel;
  can_edit?: boolean;
  can_delete?: boolean;
  can_delegate?: boolean;
}

// ============================================================================
// GRANT (Permission delegation)
// ============================================================================

export interface MetadataNodeGrant {
  _id: string;
  grantee_type: 'role' | 'user';
  grantee_id: string;
  grantee_name?: string; // Populated
  organization_id: string | null;
  target_type: 'domain' | 'tree' | 'node';
  target_id: string;
  target_name?: string; // Populated
  level: GrantLevel;
  cascade: boolean;
  granted_by: string;
  granted_by_name?: string; // Populated
  ceiling_grant_id: string | null;
  created_at: string;
  updated_at: string;
  expires_at?: string;
}

export interface CreateGrantData {
  grantee_type: 'role' | 'user';
  grantee_id: string;
  target_type: 'domain' | 'tree' | 'node';
  target_id: string;
  level: GrantLevel;
  cascade?: boolean;
}

// ============================================================================
// VALUE (Custom values per scope)
// ============================================================================

export interface MetadataNodeValue {
  _id: string;
  node_id: string;
  scope_type: ScopeType;
  scope_id: string | null;
  custom_name?: string;
  custom_description?: string;
  metadata: Record<string, unknown>;
  is_enabled: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateValueData {
  node_id: string;
  scope_type: ScopeType;
  scope_id?: string | null;
  custom_name?: string;
  custom_description?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateValueData {
  custom_name?: string;
  custom_description?: string;
  metadata?: Record<string, unknown>;
  is_enabled?: boolean;
}

// ============================================================================
// EFFECTIVE PERMISSION (from check-permission endpoint)
// ============================================================================

export interface EffectivePermission {
  level: GrantLevel;
  source: 'direct' | 'inherited' | 'cascade' | 'base';
  grant_id: string | null;
  can_delegate_to: GrantLevel;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface CreateDomainData {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  order?: number;
}

export interface UpdateDomainData {
  name?: string;
  description?: string;
  icon?: string;
  order?: number;
}

export interface CreateTreeData {
  id: string;
  domain_id: string;
  name: string;
  description?: string;
  base_permission?: BasePermission;
  order?: number;
}

export interface UpdateTreeData {
  name?: string;
  description?: string;
  base_permission?: BasePermission;
  order?: number;
}

export interface CreateNodeData {
  id: string;
  tree_id: string;
  parent_id?: string | null;
  name: string;
  description?: string;
  base_permission?: BasePermission;
  order?: number;
}

export interface UpdateNodeData {
  name?: string;
  description?: string;
  base_permission?: BasePermission;
  order?: number;
}

export interface MoveNodeData {
  parent_id: string | null;
  order?: number;
}

// ============================================================================
// HELPERS
// ============================================================================

export const GRANT_LEVEL_LABELS: Record<GrantLevel, string> = {
  none: 'Sin acceso',
  read: 'Lectura',
  write: 'Escritura',
  admin: 'Administrador',
};

export const GRANT_LEVEL_COLORS: Record<GrantLevel, string> = {
  none: 'gray',
  read: 'blue',
  write: 'green',
  admin: 'purple',
};

export const BASE_PERMISSION_LABELS: Record<BasePermission, string> = {
  system: 'Sistema',
  organization: 'Organizacion',
  user: 'Usuario',
};

export const BASE_PERMISSION_COLORS: Record<BasePermission, string> = {
  system: 'gray',
  organization: 'blue',
  user: 'green',
};

/**
 * Check if a grant level allows a specific action
 */
export function canPerformAction(
  level: GrantLevel,
  action: 'read' | 'write' | 'delete' | 'delegate'
): boolean {
  const levelValue: Record<GrantLevel, number> = {
    none: 0,
    read: 1,
    write: 2,
    admin: 3,
  };

  const requiredLevel: Record<string, number> = {
    read: 1,
    write: 2,
    delete: 3,
    delegate: 3,
  };

  return levelValue[level] >= requiredLevel[action];
}

/**
 * Build tree structure from flat node list
 */
export function buildNodeTree(nodes: MetadataNode[]): MetadataNode[] {
  const nodeMap = new Map<string, MetadataNode>();
  const rootNodes: MetadataNode[] = [];

  // First pass: create map
  nodes.forEach((node) => {
    nodeMap.set(node._id, { ...node, children: [] });
  });

  // Second pass: build tree
  nodes.forEach((node) => {
    const currentNode = nodeMap.get(node._id)!;
    if (node.parent_id) {
      const parent = nodeMap.get(node.parent_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(currentNode);
      }
    } else {
      rootNodes.push(currentNode);
    }
  });

  // Sort by order
  const sortByOrder = (a: MetadataNode, b: MetadataNode) => a.order - b.order;
  rootNodes.sort(sortByOrder);
  rootNodes.forEach((node) => {
    if (node.children) {
      node.children.sort(sortByOrder);
    }
  });

  return rootNodes;
}
